// Utility functions for GeoMarkup application
import { v4 as uuidv4 } from 'uuid';
import {
  MIN_LATITUDE,
  MAX_LATITUDE,
  MIN_LONGITUDE,
  MAX_LONGITUDE,
} from '../constants/geolocation';

// ===== ID GENERATION =====

export const generateId = (): string => {
  return uuidv4(); // UUID v4 for backend compatibility
};

// ===== DATE UTILITIES =====
// Date functions have been moved to:
// - useLocalizedDate hook for i18n-aware formatting
// - dateHelpers.ts for pure date calculations
// This maintains backward compatibility for any legacy imports

// ===== STRING UTILITIES =====

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substr(0, maxLength) + '...';
};

export const capitalizeFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const lowerFirst = (str: string): string => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

// ===== STRING UTILITIES =====

/**
 * Extract initials from a display name (max 2 letters, uppercase).
 */
export function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const firstPart = parts[0];
    return firstPart ? firstPart.slice(0, 2).toUpperCase() : '?';
  }
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
}

// ===== COORDINATE UTILITIES =====

export const formatCoordinates = (lat: number, lng: number): string => {
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';

  return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
};

// ===== VALIDATION UTILITIES =====

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const emailsMatch = (
  left: string,
  right: string | null | undefined
): boolean => {
  if (!right) return false;
  return left.trim().toLowerCase() === right.trim().toLowerCase();
};

export const isValidCoordinates = (lat: number, lng: number): boolean => {
  return (
    lat >= MIN_LATITUDE &&
    lat <= MAX_LATITUDE &&
    lng >= MIN_LONGITUDE &&
    lng <= MAX_LONGITUDE
  );
};

// ===== COLLECTION UTILITIES =====

export function extractUniqueSortedStrings<T>(
  items: T[],
  accessor: (item: T) => string | null | undefined
): string[] {
  const values = new Set(
    items
      .map(accessor)
      .filter((val): val is string => val != null && val.trim().length > 0)
  );
  return Array.from(values).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
}

// ===== ARRAY UTILITIES =====

export const sortBy = <T>(
  array: T[],
  key: keyof T,
  direction: 'asc' | 'desc' = 'asc'
): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

export const unique = <T>(array: T[]): T[] => {
  return [...new Set(array)];
};

// ===== DEBOUNCE UTILITY =====

export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: number;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = window.setTimeout(() => func(...args), wait);
  };
};

// ===== THROTTLE UTILITY =====

export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): T => {
  let inThrottle: boolean;

  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), wait);
    }
  }) as T;
};

// ===== LOCAL STORAGE UTILITIES =====

export const localStorage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Handle storage quota exceeded or other errors
      console.warn('Failed to save to localStorage');
    }
  },

  remove: (key: string): void => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      console.warn('Failed to remove from localStorage');
    }
  },

  clear: (): void => {
    try {
      window.localStorage.clear();
    } catch {
      console.warn('Failed to clear localStorage');
    }
  },
};

// ===== ERROR HANDLING =====

export const handleError = (error: unknown, context?: string): void => {
  const message =
    error instanceof Error ? error.message : 'An unknown error occurred';
  const contextMessage = context ? `${context}: ${message}` : message;

  console.error(contextMessage, error);

  // Here you could add error reporting service integration
  // reportError(contextMessage, error);
};

// ===== ASYNC UTILITIES =====

export const delay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const retry = async <T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delayMs: number = 1000
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await delay(delayMs);
      return retry(fn, retries - 1, delayMs);
    }
    throw error;
  }
};

// ===== CSS CLASS UTILITIES =====

export const classNames = (
  ...classes: (string | undefined | null | false)[]
): string => {
  return classes.filter(Boolean).join(' ');
};

// ===== RESPONSIVE UTILITIES =====
// Responsive detection has been moved to:
// - src/utils/deviceDetection.ts for core detection functions
// - useDeviceLayout hook for reactive device state
// This maintains backward compatibility for any legacy imports
